import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import { formatImageUrl } from '../utils/formatImageUrl';

const getImageUrl = (imagePath) => {
    return formatImageUrl(imagePath, '/assets/images/thumbs/about-img1.png');
};

const About = () => {
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await api.get('/api/settings');
                setSettings(res.data || {});
            } catch (err) {
                console.error("Failed to fetch settings", err);
            }
        };
        fetchSettings();
    }, []);

    // Fallbacks
    const aboutTitle = settings?.aboutTitle || 'Nourishing Lives with Pure, Natural Goodness';
    const aboutSubtitle = settings?.aboutSubtitle || 'Our Mission';
    const aboutDescPrimary = settings?.aboutDescPrimary || 'Welcome to Healthy Home, where tradition meets purity. We believe that true well-being starts from the roots—which is why we are dedicated to bringing you the highest quality unrefined, cold-pressed oils, traditional sweeteners, natural salts, and farm-fresh dairy.';
    const aboutDescSecondary = settings?.aboutDescSecondary || "Whether you're looking for our signature Cold Pressed Groundnut Oil or our pure Himalayan Rock Salt, every product we craft is made with care, sustainability, and absolute transparency. Our journey began with a simple idea: to make healthy, chemical-free living accessible to every family.";
    const aboutImageUrl = getImageUrl(settings?.aboutImageUrl);
    const aboutFeature1 = settings?.aboutFeature1 || '100% Organic';
    const aboutFeature2 = settings?.aboutFeature2 || 'Premium Quality';
    const aboutBtnText = settings?.aboutBtnText || 'Shop Now';

    const missionTitle = settings?.missionTitle || 'Our Core Values';
    const missionSubtitle = settings?.missionSubtitle || 'What We Stand For';
    const missionDesc = settings?.missionDesc || 'Everything we do is guided by a simple set of principles that keep us honest to our roots.';

    const values = [
        {
            id: 1,
            icon: 'ph-plant',
            title: settings?.missionGridCol1Title || 'Sourced Responsibly',
            description: settings?.missionGridCol1Desc || 'We work directly with small farms and trusted producers, cutting out middlemen so quality and fairness stay intact.',
        },
        {
            id: 2,
            icon: 'ph-flask',
            title: settings?.missionGridCol2Title || 'No Additives, Ever',
            description: settings?.missionGridCol2Desc || 'Every product is free from preservatives, refined chemicals, and artificial shortcuts — just what nature intended.',
        },
        {
            id: 3,
            icon: 'ph-recycle',
            title: settings?.missionGridCol3Title || 'Sustainable Practices',
            description: settings?.missionGridCol3Desc || 'From cold-pressing methods to eco-friendly packaging, we minimize our footprint at every step of the process.',
        },
        {
            id: 4,
            icon: 'ph-hand-heart',
            title: settings?.missionGridCol4Title || 'Community First',
            description: settings?.missionGridCol4Desc || 'A share of every purchase supports the farming communities and artisans who make our products possible.',
        },
    ];

    const stats = [
        { id: 1, icon: 'ph-package', value: settings?.stat1Count || '50+', label: settings?.stat1Label || 'Natural Products' },
        { id: 2, icon: 'ph-users-three', value: settings?.stat2Count || '25,000+', label: settings?.stat2Label || 'Happy Families' },
        { id: 3, icon: 'ph-farm', value: settings?.stat3Count || '120+', label: settings?.stat3Label || 'Partner Farms' },
        { id: 4, icon: 'ph-calendar-check', value: settings?.stat4Count || '8', label: settings?.stat4Label || 'Years of Trust' },
    ];

    const ctaTitle = settings?.ctaTitle || 'Ready to Taste the Difference?';
    const ctaDesc = settings?.ctaDesc || 'Explore our full range of cold-pressed oils, natural salts, and traditional sweeteners, crafted for a healthier home.';
    const ctaBtn1Text = settings?.ctaBtn1Text || 'Shop Now';
    const ctaBtn2Text = settings?.ctaBtn2Text || 'Get in Touch';

    return (
        <>
            {/* Intro / Mission */}
            <section className="about-section py-80">
                <div className="container container-lg">
                    <div className="row gy-5 align-items-center">
                        <div className="col-lg-6 wow fadeInLeft" data-wow-duration="0.8s">
                            <div className="about-thumb position-relative d-flex justify-content-center">
                                <img
                                    src={aboutImageUrl}
                                    alt="About Us"
                                    className="rounded-16 w-100 object-fit-cover hover-lift"
                                    style={{ maxHeight: 460, minHeight: 260, height: 'clamp(260px, 45vw, 460px)', boxShadow: '0 20px 40px rgba(0,0,0,0.08)' }}
                                />
                            </div>
                        </div>
                        <div className="col-lg-6 ps-xl-5 wow fadeInRight" data-wow-duration="0.8s">
                            <div className="about-content">
                                <span
                                    className="text-main-600 fw-semibold text-uppercase mb-16 d-block"
                                    style={{ letterSpacing: '1px' }}
                                >
                                    {aboutSubtitle}
                                </span>
                                <h2 className="mb-24 fw-bold">{aboutTitle}</h2>
                                <p className="text-gray-700 mb-24" style={{ fontSize: 17, lineHeight: 1.8,textAlign:"justify" }}>
                                    {aboutDescPrimary}
                                </p>
                                <p className="text-gray-700 mb-32" style={{ fontSize: 17, lineHeight: 1.8,textAlign:"justify"  }}>
                                    {aboutDescSecondary}
                                </p>
                                <div className="d-flex align-items-center flex-wrap gap-24 mb-40">
                                    <div className="d-flex align-items-center gap-16 wow fadeInUp" data-wow-delay="0.1s">
                                        <span className="w-56 h-56 flex-center rounded-circle bg-main-50 text-main-600 text-2xl">
                                            <i className="ph ph-leaf" />
                                        </span>
                                        <div>
                                            <h6 className="mb-0 text-md">{aboutFeature1}</h6>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-16 wow fadeInUp" data-wow-delay="0.2s">
                                        <span className="w-56 h-56 flex-center rounded-circle bg-main-50 text-main-600 text-2xl">
                                            <i className="ph ph-shield-check" />
                                        </span>
                                        <div>
                                            <h6 className="mb-0 text-md">{aboutFeature2}</h6>
                                        </div>
                                    </div>
                                </div>
                                <Link
                                    to="/shop"
                                    className="btn btn-main py-16 px-32 rounded-pill d-inline-flex align-items-center gap-8 hover-lift"
                                >
                                    <i className="ph ph-shopping-bag text-xl" />
                                    {aboutBtnText}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Bar */}
            <section className="about-stats py-64" style={{ background: '#F6FAF7' }}>
                <div className="container container-lg">
                    <div className="row g-4">
                        {stats.map((s, idx) => (
                            <div className="col-6 col-md-3 wow fadeInUp" data-wow-delay={`${idx * 0.1}s`} key={s.id}>
                                <div className="text-center">
                                    <span
                                        className="flex-center mx-auto mb-16 rounded-circle bg-white text-main-600 hover-lift"
                                        style={{ width: 64, height: 64, boxShadow: '0 8px 20px rgba(0,0,0,0.06)' }}
                                    >
                                        <i className={`ph ${s.icon}`} style={{ fontSize: 26 }} />
                                    </span>
                                    <h3 className="mb-4 fw-bold">{s.value}</h3>
                                    <span className="text-gray-600 text-sm">{s.label}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Values */}
            <section className="about-values py-80">
                <div className="container container-lg">
                    <div className="text-center mb-56 wow fadeInUp" style={{ maxWidth: 640, margin: '0 auto' }}>
                        <span
                            className="text-main-600 fw-semibold text-uppercase mb-16 d-block"
                            style={{ letterSpacing: '1px' }}
                        >
                            {missionSubtitle}
                        </span>
                        <h2 className="mb-16 fw-bold">{missionTitle}</h2>
                        <p className="text-gray-700">
                            {missionDesc}
                        </p>
                    </div>
                    <div className="row g-4">
                        {values.map((v, idx) => (
                            <div className="col-lg-3 col-sm-6 wow fadeInUp" data-wow-delay={`${idx * 0.1}s`} key={v.id}>
                                <div className="h-100 p-32 rounded-16 border border-gray-100 text-center hover-border-main-600 hover-lift transition-2">
                                    <span
                                        className="flex-center mx-auto mb-20 rounded-circle bg-main-50 text-main-600"
                                        style={{ width: 64, height: 64 }}
                                    >
                                        <i className={`ph ${v.icon}`} style={{ fontSize: 26 }} />
                                    </span>
                                    <h6 className="mb-12">{v.title}</h6>
                                    <p className="text-gray-600 text-sm mb-0" style={{ lineHeight: 1.7 }}>
                                        {v.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Banner */}
            <section className="about-cta py-64">
                <div className="container container-lg">
                    <div
                        className="rounded-16 p-48 text-center wow zoomIn"
                        data-wow-duration="0.8s"
                        style={{ background: 'linear-gradient(135deg, #E9F7EF 0%, #F6FAF7 100%)' }}
                    >
                        <h3 className="mb-16 fw-bold">{ctaTitle}</h3>
                        <p className="text-gray-700 mb-32" style={{ maxWidth: 520, margin: '0 auto 32px' }}>
                            {ctaDesc}
                        </p>
                        <div className="d-flex align-items-center justify-content-center gap-16 flex-wrap">
                            <Link
                                to="/shop"
                                className="btn btn-main py-16 px-32 rounded-pill d-inline-flex align-items-center gap-8 hover-lift"
                            >
                                <i className="ph ph-shopping-bag text-xl" />
                                {ctaBtn1Text}
                            </Link>
                            <Link
                                to="/contact"
                                className="btn py-16 px-32 rounded-pill d-inline-flex align-items-center gap-8 border border-main-600 text-main-600 hover-lift"
                            >
                                <i className="ph ph-chat-circle-text text-xl" />
                                {ctaBtn2Text}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default About;